import { Link } from 'react-router-dom'
import { FiAlertTriangle, FiCheckCircle, FiCalendar, FiZap } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'

const toc = [
  { id: 'contexto', label: 'Contexto: por qué un nuevo decreto en 2026' },
  { id: 'que-cambia', label: 'Qué cambia respecto al RD-ley 8/2019' },
  { id: 'requisitos-tecnicos', label: 'Requisitos técnicos del nuevo decreto' },
  { id: 'interoperabilidad', label: 'Interoperabilidad con la ITSS' },
  { id: 'fechas-clave', label: 'Calendario de aplicación' },
  { id: 'que-hacer-ya', label: 'Qué hacer ya en tu empresa' },
  { id: 'consecuencias', label: 'Consecuencias de no adaptarse' },
  { id: 'faq', label: 'Preguntas frecuentes' },
]

const faqs = [
  { question: '¿El nuevo Real Decreto sustituye al RD-ley 8/2019?', answer: 'No. Lo complementa. El RD-ley 8/2019 sigue siendo la base legal del registro de jornada (modifica el art. 34 ET). El nuevo decreto desarrolla los aspectos técnicos del soporte digital y endurece la trazabilidad.' },
  { question: '¿Tengo que cambiar de proveedor si ya uso un sistema digital?', answer: 'Solo si tu sistema actual no cumple los nuevos requisitos: trazabilidad inmutable, exportación normalizada, acceso del trabajador desde su perfil y cumplimiento RGPD. Pregunta a tu proveedor por su roadmap de adaptación.' },
  { question: '¿Qué pasa con las hojas Excel y registros en papel?', answer: 'El decreto pivota explícitamente hacia el soporte digital. El papel y los Excel siguen siendo aceptables a efectos del 34.9 ET, pero quedan en posición precaria: cualquier inspección presumirá insuficiencia y exigirá indicios reforzados.' },
  { question: '¿Aplica a todas las empresas o hay umbrales?', answer: 'Aplica a todas las empresas con trabajadores por cuenta ajena. No hay umbrales por número de empleados. Los plazos de adaptación pueden ser distintos para microempresas, pero la obligación es universal.' },
  { question: '¿Cuándo entra en vigor?', answer: 'La aprobación está prevista para 2026 con periodo de adaptación de 6 a 12 meses. Las empresas que ya tengan un sistema digital con trazabilidad probablemente solo necesitarán actualizaciones menores. Las que aún usen papel/Excel tendrán que migrar.' },
]

export default function RealDecreto2026FichajeDigital() {
  return (
    <BlogLayout slug="real-decreto-2026-fichaje-digital" toc={toc} faqs={faqs}>
      <section id="contexto" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Contexto: por qué un nuevo decreto en 2026</h2>
        <p>
          Desde la entrada en vigor del RD-ley 8/2019 han pasado seis años. La práctica ha demostrado dos cosas: el cumplimiento real está muy lejos del 100 % (<Link to="/blog/inspeccion-trabajo-control-horario">la ITSS detecta incumplimientos en el 35-40 % de las visitas</Link>), y la prueba en muchas empresas es manipulable y poco fiable.
        </p>
        <p>
          El nuevo Real Decreto sobre fichaje digital, anunciado por el Ministerio de Trabajo, busca cerrar esa brecha con tres vectores: <strong>obligatoriedad práctica del soporte digital</strong>, <strong>trazabilidad inmutable</strong> y <strong>acceso telemático directo</strong> de la Inspección.
        </p>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg my-6">
          <p className="text-blue-900 text-sm"><strong>Aviso:</strong> el texto definitivo está en tramitación. La información de este artículo se basa en el anteproyecto y en declaraciones públicas. Iremos actualizando según se publiquen versiones oficiales.</p>
        </div>
      </section>

      <section id="que-cambia" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Qué cambia respecto al RD-ley 8/2019</h2>
        <div className="overflow-x-auto my-6">
          <table className="min-w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-semibold text-gray-900">Aspecto</th>
                <th className="text-left p-3 font-semibold text-gray-900">RD-ley 8/2019</th>
                <th className="text-left p-3 font-semibold text-gray-900">Nuevo Real Decreto 2026</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="p-3 font-medium">Soporte</td><td className="p-3 text-gray-600">Cualquiera</td><td className="p-3 text-gray-600">Pivota hacia digital con trazabilidad</td></tr>
              <tr><td className="p-3 font-medium">Datos mínimos</td><td className="p-3 text-gray-600">Inicio y fin</td><td className="p-3 text-gray-600">Inicio, fin, pausas y eventos relevantes</td></tr>
              <tr><td className="p-3 font-medium">Trazabilidad</td><td className="p-3 text-gray-600">No exigida explícitamente</td><td className="p-3 text-gray-600">Auditoría inmutable de cambios</td></tr>
              <tr><td className="p-3 font-medium">Acceso ITSS</td><td className="p-3 text-gray-600">A petición</td><td className="p-3 text-gray-600">Directo y telemático</td></tr>
              <tr><td className="p-3 font-medium">Acceso del trabajador</td><td className="p-3 text-gray-600">A petición</td><td className="p-3 text-gray-600">Permanente desde su perfil</td></tr>
              <tr><td className="p-3 font-medium">Exportación</td><td className="p-3 text-gray-600">No regulada</td><td className="p-3 text-gray-600">Formato normalizado obligatorio</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="requisitos-tecnicos" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Requisitos técnicos del nuevo decreto</h2>
        <p>El sistema con el que registres la jornada deberá garantizar:</p>
        <ul className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <FiZap className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Inalterabilidad.</strong> Los registros no pueden modificarse silenciosamente. Cualquier corrección queda registrada como evento separado con autor, fecha y motivo.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiZap className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Marca temporal fiable.</strong> Sello de tiempo del servidor, no del dispositivo del trabajador. Sincronización con NTP.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiZap className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Identificación inequívoca</strong> del trabajador en cada fichaje (PIN, biometría, sesión autenticada).</div>
          </li>
          <li className="flex items-start gap-3">
            <FiZap className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Conservación 4 años</strong> en almacenamiento seguro, con backup y plan de continuidad.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiZap className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Exportación en formato normalizado</strong> (probablemente XML/JSON con esquema oficial) que la ITSS pueda procesar automáticamente.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiZap className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Cumplimiento RGPD reforzado</strong> con DPIA, encargado de tratamiento documentado y derechos ARCO accesibles.</div>
          </li>
        </ul>
      </section>

      <section id="interoperabilidad" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Interoperabilidad con la ITSS</h2>
        <p>
          La novedad más rupturista es la previsión de <strong>acceso directo de la Inspección</strong> a los registros sin necesidad de visita física. Esto se traduciría en:
        </p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Endpoint estandarizado al que la ITSS puede solicitar registros con autenticación reforzada.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Plazo máximo de respuesta de 24-48 horas (en visita presencial: inmediato).</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Trazabilidad de la consulta: el trabajador puede saber qué actuaciones se hicieron sobre sus datos.</li>
        </ul>
        <p>
          La consecuencia inmediata es que la ITSS podrá hacer cribados masivos por sectores y geografías con un equipo reducido. Las inspecciones se multiplicarán y se afinarán.
        </p>
      </section>

      <section id="fechas-clave" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Calendario de aplicación</h2>
        <p>Aunque las fechas son tentativas, este es el escenario más probable:</p>
        <div className="space-y-3 my-6">
          <div className="flex items-start gap-3 p-4 border border-gray-100 rounded-lg">
            <FiCalendar className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-900">2026 (1er semestre) — Aprobación</p>
              <p className="text-sm text-gray-600">Publicación del Real Decreto definitivo en el BOE.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 border border-gray-100 rounded-lg">
            <FiCalendar className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-900">2026 (2º semestre) — Adaptación</p>
              <p className="text-sm text-gray-600">Periodo de adaptación de empresas y proveedores. Posible vacatio legis de 6-12 meses.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 border border-gray-100 rounded-lg">
            <FiCalendar className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-900">2027 — Vigencia plena</p>
              <p className="text-sm text-gray-600">Inicio de inspecciones bajo el nuevo régimen y posibles sanciones agravadas por los nuevos incumplimientos.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="que-hacer-ya" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Qué hacer ya en tu empresa</h2>
        <ol className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
            <div><strong>Audita tu sistema actual.</strong> ¿Tiene auditoría inmutable? ¿Permite exportar a CSV/PDF firmado? Si la respuesta es no, planifica migración.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
            <div><strong>Pregunta a tu proveedor por el roadmap de adaptación.</strong> Si no tiene plan publicado, mal asunto.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
            <div><strong>Revisa la información a tus trabajadores.</strong> El nuevo decreto refuerza la transparencia: consulta y firma de la representación legal.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
            <div><strong>Documenta el procedimiento interno.</strong> Cómo se ficha, qué se hace si hay olvido, cómo se solicitan correcciones.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
            <div><strong>Si aún usas papel/Excel, migra ya.</strong> No esperes a la entrada en vigor: la inspección está endureciendo el criterio en paralelo a la tramitación.</div>
          </li>
        </ol>
      </section>

      <section id="consecuencias" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Consecuencias de no adaptarse</h2>
        <p>
          La no adaptación tendrá tres consecuencias concretas:
        </p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiAlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" /> <strong>Más probabilidad de inspección</strong> al permitir cribados telemáticos masivos.</li>
          <li className="flex items-start gap-2"><FiAlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" /> <Link to="/blog/sanciones-no-fichar-2026"><strong>Sanciones agravadas</strong></Link> al incumplir requisitos técnicos específicos del nuevo decreto.</li>
          <li className="flex items-start gap-2"><FiAlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" /> <strong>Posición probatoria debilitada</strong> en reclamaciones de horas extras, despidos disciplinarios y procesos colectivos.</li>
        </ul>
      </section>

      <section id="faq" className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.question}>
              <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
              <p className="text-gray-600 text-sm">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-gray-100 pt-8 mt-12 text-sm text-gray-500">
        Si quieres adelantarte al nuevo Real Decreto:
        <Link to="/fichaje-digital" className="text-blue-600 hover:underline mx-1">página de fichaje digital</Link>
        ·
        <Link to="/blog/como-elegir-sistema-fichaje" className="text-blue-600 hover:underline mx-1">checklist para elegir sistema</Link>.
      </div>
    </BlogLayout>
  )
}
